<template>
	<card :title="options.title || report.type">
		<template #default>
			<div v-if="editing">
				<div v-if="$slots['pre-date-filter']" class="form-row mb-4">
					<slot name="pre-date-filter"></slot>
				</div>
				<div class="form-row mb-4">
					<o-field label="Date filter">
						<o-select v-model="activeReport.filter.type" expanded class="date-filter-type">
							<option v-for="option in timeTypes" :key="option.Id" :value="option.Id">
								{{ option.Name }}
							</option>
						</o-select>
					</o-field>
				</div>
				<div v-if="activeReport.filter.type != 'none'" class="mb-4">
					<o-field label="Date range">
						<date-filter
							v-model="activeReport.filter.date"
							:reportType="activeReport.type"></date-filter>
					</o-field>
				</div>
				<div class="form-row">
					<search-filter v-model="activeReport.filter.keys" :type="filterType"></search-filter>

					<o-button
						class="runReportBtn"
						variant="primary"
						icon-left="cog"
						:loading="report.cancelToken ? false : report.loading"
						:disabled="report.cancelToken ? false : report.loading"
						@click="GetReport">
						Generate Report
					</o-button>
				</div>
				<div class="form-row">
					<slot></slot>
				</div>
			</div>
				<div v-else>
					<div class="is-flex is-justify-content-space-between">
						<div class="is-flex is-flex-direction-column">
							<div>
								<o-field :label="timeTypeLabel">
								{{
									moment.utc(report.filter.date.startDate).format("MM/DD/YYYY") +
									" - " +
									moment.utc(report.filter.date.endDate).format("MM/DD/YYYY")
									}}
								</o-field>
							</div>
							<div v-if="$slots.summary" class="report-summary-inline">
								<slot name="summary"></slot>
							</div>

							<div>
								<o-field label="Filters" v-if="report.filter.keys && report.filter.keys.length > 0">
								<div
									class="tag mr-2 my-2 normal-font-size"
									v-for="(kv, index) in report.filter.keys"
									:key="index">
									<span class="mr-2">{{ kv.key?.Text }}:</span>
									<span class="mr-2">
										{{ kv.value?.Text }}
									</span>
								</div>
								</o-field>
							</div>
						</div>
						<div class="is-flex is-flex-direction-column">
							<div class="mb-4">
								<o-field label="Report generated from cache" v-if="report.lastCached">
								<div class="mr-2">{{ report.lastCached.local().format("MM/DD/YYYY h:mm a") }}</div>
								<o-button
									class="refreshBtn"
									:disabled="report.loading"
									size="small"
									icon-left="sync-alt"
									@click="RefreshCache">
									Refresh
								</o-button>
							</o-field>
						</div>
						<div class="form-row form-row-right">
							<o-button class="modifyBtn" variant="primary" icon-left="pencil" @click="EditReport">
								Modify Report
							</o-button>
							</div>
						</div>
					</div>
					<div>
						<slot></slot>
					</div>
				</div>
			</template>
		<template #actions>
			<slot name="actions"></slot>
			<share
				class="is-pulled-right"
				:report="report"
				:exporting="exporting"
				:hide="options.share === false || !report.results?.length"
				:excel="
					(options.excel && options.headers && options.headers.length > 0) ||
					(options.excel && Array.isArray(options.sheets))
				"
				@excel="Export"></share>
		</template>
	</card>
</template>
<script>
	import { ref, computed, watch, inject, onBeforeUnmount } from "vue";
	import { share, dateFilter, searchFilter } from "@/components/webComponents";
	import { serializeExportSheets } from "@/services/reportExportWorker";
	import { getExportErrorMessage, isUnexpectedExportResponse } from "@/services/reportExportErrors";

	export default {
		components: {
			share,
			dateFilter,
			searchFilter
		},
		props: {
			prepareExport: Function,
			report: {
				type: Object,
				default: () => {
					return { filter: {} };
				}
			},
			editing: {
				type: Boolean,
				default: false
			},
			autoRun: {
				type: Boolean,
				default: false
			},
			options: {
				type: Object,
				default: () => {
					return {};
				}
			}
		},
		emits: ["search", "edit", "beforeExport", "update:report"],
		setup(props, context) {
			const $http = inject("$http");
			const moment = inject("$moment");
			const $error = inject("$error");

			const exporting = ref(false);

			const filterType = computed(() => {
				switch (props.report.type) {
					case "Assignment":
						return "assignments";
					case "Company":
						return "companies";
					case "Financial":
						return "financial";
					case "User":
						return "users";
					case "License":
						return "licenses";
					case "Location":
						return "locations";
					default:
						return "none";
				}
			});
			const timeTypes = computed(() => {
				if (Array.isArray(props.options?.timeTypes) && props.options.timeTypes.length > 0) {
					return props.options.timeTypes;
				}

				switch (props.report.type) {
					case "Assignment":
						return [
							{ Id: "activity", Name: "Activity" },
							{ Id: "taken", Name: "Date Taken" },
							{ Id: "invoice", Name: "Invoice Date" },
							{ Id: "status", Name: "Status Date" },
							{ Id: "created", Name: "Created Date" },
							{ Id: "markedfilled", Name: "Marked or Filled Date" }
						];
					case "Company":
						return [
							{ Id: "none", Name: "None" },
							{ Id: "activity", Name: "Activity" },
							{ Id: "noActivity", Name: "No Activity" },
							{ Id: "invoice", Name: "Invoice" },
							{ Id: "taken", Name: "Assignment Taken" }
						];
					case "Financial":
						return [
							{ Id: "activity", Name: "Activity" },
							{ Id: "taken", Name: "Date Taken" },
							{ Id: "invoice", Name: "Invoice Date" },
							{ Id: "status", Name: "Status Date" }
						];
					case "User":
						return [
							{ Id: "none", Name: "None" },
							{ Id: "hire", Name: "Hire Date" },
							{ Id: "inactive", Name: "Inactive Date" },
							{ Id: "role", Name: "Role Changed" },
							{ Id: "roleORhire", Name: "Role Changed or Hire Date" }
						];
					case "License":
						return [
							{ Id: "none", Name: "None" },
							{ Id: "hire", Name: "Hire Date" },
							{ Id: "inactive", Name: "Inactive Date" },
							{ Id: "role", Name: "Role Changed" },
							{ Id: "roleORhire", Name: "Role Changed or Hire Date" }
						];
					case "Location":
						return [
							{ Id: "none", Name: "None" },
							{ Id: "hire", Name: "Hire Date" },
							{ Id: "inactive", Name: "Inactive Date" },
							{ Id: "role", Name: "Role Changed" },
							{ Id: "roleORhire", Name: "Role Changed or Hire Date" }
						];
					default:
						return [];
				}
			});
			const timeTypeLabel = computed(() => {
				return timeTypes.value.find((x) => x.Id == props.report.filter.type)?.Name || "None";
			});
			const activeReport = ref(props.report);

			// const DeleteCache = async () => {
			// 	try {
			// 		const copy = JSON.parse(JSON.stringify(props.report));

			// 		copy.results = [];
			// 		copy.compare = [];

			// 		await $http.delete("/api/v2/report/cache", { data: { report: copy } });
			// 	} catch (err) {
			// 		console.error(err);
			// 	}
			// };

			const GetReport = () => {
				context.emit("search");
			};

			const RefreshCache = async () => {
				//await DeleteCache();
				context.emit("search", { refresh: true });
			};

			const EditReport = () => {
				if (activeReport.value.cancelToken) activeReport.value.cancelToken.cancel("Editing.");
				context.emit("edit");
			};

			const SyncReport = () => {
				//context.emit("update:report", activeReport.value);
				activeReport.value.filter = props.report.filter;
			};

			let activeExport;
			let activeRequest;
			onBeforeUnmount(() => {
				activeExport?.abort();
				activeRequest?.cancel("Leaving report");
			});

			const Export = async () => {
				if (exporting.value) return;
				exporting.value = true;
				const controller = new AbortController();
				activeExport = controller;
				let stage = "preparing";
				try {
					// Yield before any report-specific formatting so the spinner can paint.
					await new Promise(resolve => setTimeout(resolve, 0));
					if (controller.signal.aborted) return;
					let prepared;
					if (props.prepareExport) {
						prepared = await props.prepareExport(controller.signal);
					} else {
						context.emit("beforeExport");
					}
					if (controller.signal.aborted) return;
					const body = prepared instanceof Blob ? prepared : await serializeExportSheets(
						prepared || props.options.sheets || [{
							title: props.report.type,
							headers: props.options.headers || [],
							rows: props.report.results
						}], controller.signal);
					if (controller.signal.aborted) return;
					const filename = moment().format("MMDDYYYY-hhmm") + " " + props.report.type + " report.xlsx";
					stage = "request";
					activeRequest = $http.CancelToken.source();
					const response = await $http.post("/api/v2/report/xlsx", body, {
						responseType: "blob",
						headers: { "Content-Type": "application/json" },
						transformRequest: [(data) => data],
						cancelToken: activeRequest.token,
						skipNetworkErrorRedirect: true
					});
					if (controller.signal.aborted) return;
					if (isUnexpectedExportResponse(response)) {
						throw Object.assign(new Error("Unexpected export response"), { code: "EXPORT_INVALID_RESPONSE", response });
					}
					stage = "download";
					const link = document.createElement("a");
					const url = window.URL.createObjectURL(response.data);
					try {
						link.href = url;
						link.setAttribute("download", filename);
						document.body.appendChild(link);
						link.click();
					} finally {
						link.remove();
						setTimeout(() => window.URL.revokeObjectURL(url), 1000);
					}
				} catch (err) {
					if (!controller.signal.aborted) {
						const message = await getExportErrorMessage(err, stage);
						if (!controller.signal.aborted) $error(message, 10000);
					}
				} finally {
					activeExport = undefined;
					activeRequest = undefined;
					exporting.value = false;
				}
			};

			let hasRan = false;
			watch(
				() => props.report.filter,
				(n, o) => {
					SyncReport();

					if (props.autoRun && !hasRan) {
						hasRan = true;
						GetReport();
					}
				},
				{ deep: true, immediate: true }
			);

			return {
				moment,
				exporting,
				filterType,
				timeTypes,
				timeTypeLabel,
				activeReport,
				GetReport,
				EditReport,
				RefreshCache,
				Export
			};
		}
	};
</script>
<style lang="scss" scoped>
	@use "@/assets/css/globalVars.scss" as *;

	.normal-font-size {
		font-size: 1rem;
	}

	.form-row {
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		align-items: flex-start;
		justify-content: flex-start;
		gap: $margin-element-medium;
		width: 100%;

		&:nth-child(2) {
			align-items: flex-end;
			justify-content: space-between;
		}
	}

	.form-row-right {
		justify-content: flex-end;
		align-items: flex-end;

		&:nth-child(2) {
			align-items: flex-end;
			justify-content: flex-end;
		}
	}

	.report-summary-inline {
		margin: 0.25rem 0 0.5rem 0;
	}
</style>
